if (!window.Eurus.loadedScript.has('reviews-showcase.js')) {
  window.Eurus.loadedScript.add('reviews-showcase.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xReviewsShowcase', () => ({
        loading: false,
        error: false,
        filterError: false,
        requestGeneration: 0,
        loadedOnce: false,

        reviews: [],
        products: [],
        page: 0,

        activeProduct: '',
        activeProductLabel: '',
        committedProduct: '',
        committedProductLabel: '',

        productOpen: false,

        cfg: {
          storeKey: '',
          sort: 'date_desc',
          verifiedOnly: false,
          unavailableProductLabel: 'Product unavailable'
        },

        init() {
          const root = this.$el;
          this.cfg.storeKey = root.dataset.storeKey || '';
          this.cfg.sort = root.dataset.sort || 'date_desc';
          this.cfg.verifiedOnly = root.dataset.verifiedOnly === 'true';
          this.cfg.unavailableProductLabel = root.dataset.unavailableProductLabel || 'Product unavailable';
          this.fetchReviews();
        },

        get filteredReviews() {
          let list = this.reviews.filter((review) => review.rating === 4 || review.rating === 5);
          if (this.cfg.verifiedOnly) {
            list = list.filter((review) => review.verified);
          }
          const sort = this.cfg.sort;
          return [...list].sort((a, b) => {
            if (sort === 'rating_desc') return b.rating - a.rating || b.timestamp - a.timestamp;
            if (sort === 'rating_asc') return a.rating - b.rating || b.timestamp - a.timestamp;
            return b.timestamp - a.timestamp;
          });
        },

        get visibleReviews() {
          return this.filteredReviews.slice(0, 6);
        },

        get hasActiveFilters() {
          return Boolean(this.activeProduct);
        },

        buildUrl(page) {
          const params = new URLSearchParams({
            store: this.cfg.storeKey,
            type: 'product_review',
            per_page: '100',
            page: String(page)
          });
          if (this.activeProduct) params.set('sku', this.activeProduct);
          return 'https://api.reviews.io/reviews?' + params.toString();
        },

        async fetchReviews() {
          const generation = ++this.requestGeneration;
          if (!this.cfg.storeKey) {
            this.error = true;
            this.loadedOnce = true;
            return;
          }
          this.loading = true;
          this.error = false;
          this.filterError = false;
          try {
            // Stage pages locally so the six-review preview is displayed in one update.
            let page = 0;
            let reviews = [];
            let hasMore = true;
            do {
              page += 1;
              const response = await fetch(this.buildUrl(page), {
                headers: { Accept: 'application/json' }
              });
              if (!response.ok) throw new Error('HTTP ' + response.status);
              const data = await response.json();
              if (generation !== this.requestGeneration) return;
              const incoming = (Array.isArray(data.reviews) ? data.reviews : []).map((raw, index) => ({
                id: String(raw.id || `${page}-${index}`),
                rating: (typeof raw.rating === 'number' || typeof raw.rating === 'string')
                  ? Number(raw.rating) : NaN,
                comments: String(raw.comments || '').trim(),
                author: (raw.author && raw.author.name) || '',
                location: (raw.author && raw.author.location) || '',
                date: raw.date_created || '',
                timestamp: new Date(String(raw.date_created || '').replace(' ', 'T')).getTime() || 0,
                sku: String(raw.sku || '').trim(),
                productName: String(raw.product_name || '').trim(),
                verified: Boolean(raw.verified)
              }));
              reviews = reviews.concat(incoming);
              const reportedTotal = Number(data.results_count);
              const total = Number.isFinite(reportedTotal) && reportedTotal >= 0 ? reportedTotal : reviews.length;
              // Pagination counts raw results, including reviews excluded from display.
              hasMore = incoming.length > 0 && total > reviews.length;
            } while (hasMore && reviews.filter((review) =>
              (review.rating === 4 || review.rating === 5) && (!this.cfg.verifiedOnly || review.verified)
            ).length < 6);
            this.reviews = reviews;
            this.page = page;
            this.committedProduct = this.activeProduct;
            this.committedProductLabel = this.activeProductLabel;
            if (!this.activeProduct) this.buildProductList();
          } catch (err) {
            if (generation !== this.requestGeneration) return;
            if (this.page > 0) {
              this.activeProduct = this.committedProduct;
              this.activeProductLabel = this.committedProductLabel;
              this.filterError = true;
            } else {
              this.error = true;
            }
          } finally {
            if (generation === this.requestGeneration) {
              this.loading = false;
              this.loadedOnce = true;
            }
          }
        },

        buildProductList() {
          const map = new Map(this.products.map((product) => [product.sku, {
            sku: product.sku,
            productName: product.productName || ''
          }]));
          this.reviews.forEach((review) => {
            if (!review.sku) return;
            const suppliedName = review.productName.trim();
            const name = /^\d+$/.test(suppliedName) ? '' : suppliedName;
            const existing = map.get(review.sku);
            if (!existing || (!existing.productName && name)) map.set(review.sku, { sku: review.sku, productName: name });
          });
          this.products = Array.from(map.values()).map((product) => ({
            sku: product.sku,
            productName: product.productName,
            name: product.productName || `${this.cfg.unavailableProductLabel} (SKU: ${product.sku})`
          })).sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
          );
        },

        setProduct(product) {
          const sku = product ? product.sku : '';
          if (sku === this.activeProduct) {
            this.productOpen = false;
            return;
          }
          this.activeProduct = sku;
          this.activeProductLabel = product ? product.name : '';
          this.productOpen = false;
          this.fetchReviews();
        },

        retry() {
          if (this.loading) return;
          this.fetchReviews();
        },

        destroy() {
          ++this.requestGeneration;
        },

        formatDate(value) {
          if (!value) return '';
          const date = new Date(String(value).replace(' ', 'T'));
          if (isNaN(date.getTime())) return '';
          const locale = (window.Shopify && window.Shopify.locale) || undefined;
          try {
            return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
          } catch (err) {
            return '';
          }
        },

        authorInitials(name) {
          if (!name) return '·';
          const words = name.trim().split(/\s+/);
          let initials = words[0].charAt(0);
          if (words.length > 1) initials += words[words.length - 1].charAt(0);
          return initials.toUpperCase();
        },

        authorDisplayName(name) {
          if (!name) return '';
          const words = name.trim().split(/\s+/);
          if (words.length === 1) return words[0];
          return (
            words[0] +
            ' ' +
            words[words.length - 1].charAt(0).toUpperCase() +
            '.'
          );
        }
      }));
    });
  });
}
